import type {
  ClientStreamStatsPayload,
  GuestLinkDebugSnapshot,
  StandingLatencyDetector,
  StandingLatencyInputs,
  StandingLatencyObservation,
  StandingLatencyTransition,
  StandingLatencyVerdict,
  StreamHealthEventKind,
} from '@playcastdotio/PeerCore';

/** What the host knows about a peer this tick that the guest's clientStats payload does not carry. */
export type JitterBufferHostContext = {
  measurementFresh?: boolean;
  /** outbound-rtp framesPerSecond: the rate the host actually put on the wire. */
  deliveredFps?: number;
  /** Host-side RTT floor when available; falls back to the guest's own `rttFloorMs`. */
  rttFloorMs?: number;
  /** Calibrated glass-to-glass, latest reading. Null/undefined while untrusted. */
  g2gLastMs?: number | null;
  /** Calibrated glass-to-glass window minimum. Null/undefined while untrusted. */
  g2gFloorMs?: number | null;
  /** The video codec the host negotiated for this peer's send, e.g. "video/H264 42e01f". */
  sendCodec?: string;
  /** Milliseconds since the host's last accepted keyframe request; undefined before the first. */
  sinceKeyframeMs?: number;
  /** Keyframes the host withheld from this peer because its engine's jitter buffer would inflate. */
  keyframesWithheld?: number;
};

/** Standing depth at the moment of a transition, for the drain's before/after record. */
export type StandingDepth = { frames: number | null; ms: number | null };

/** `StreamHealthEvent.detail` values: primitives only, absent rather than zero-filled. */
export type JitterBufferHealthDetail = Record<string, number | string | boolean>;

export type JitterBufferDiagnosticsDeps = {
  detector: StandingLatencyDetector;
  /** Health-event sink — the StreamHealthManager bus (production, not debug-gated). */
  emitHealthEvent: (peerId: string, kind: StreamHealthEventKind, detail: JitterBufferHealthDetail) => void;
  /** Debug NDJSON sink. The caller owns the gate; this manager only decides what to say. */
  debugLog: (tag: string, fields: Record<string, unknown>) => void;
  /** Phase 5 seam: the verdict, its transition and the standing depth, once per observed tick (the drain manager acts on `began`/`ended`). */
  onStandingTransition?: (
    peerId: string,
    verdict: StandingLatencyVerdict,
    transition: StandingLatencyTransition,
    depth: StandingDepth,
  ) => void;
  nowMs?: () => number;
};

/** The last decoded frame size seen for a peer, with the depth and latency that stood beside it. */
type FrameSizeMemo = { width: number; height: number; depthFrames?: number; depthMs?: number; g2gMs?: number };

const DIAGNOSTIC_FIELDS: ReadonlyArray<keyof ClientStreamStatsPayload> = [
  'jitterBufferDelayMs',
  'jitterBufferTargetDelayMs',
  'jitterBufferMinimumDelayMs',
  'standingBacklogFrames',
  'receiverHint',
  'decoderImplementation',
  'codecMimeType',
  'guestEngine',
  'frameWidth',
  'framePipeline',
  'jitterMs',
  'encodedFrames',
  'audioJitterBufferDelayMs',
  'presentation',
  'receiverCounters',
];

/**
 * JitterBufferDiagnosticsManager — names the standing receive-side buffer on every channel.
 *
 * The Android field shape (2026-09-19): a 21-frame buffer stood at ~320 ms glass-to-glass for
 * 45 s while every meter read healthy and the budget filed the floor as "route". This manager
 * owns the host's `StandingLatencyDetector` and puts what it sees on two channels:
 * - `[JitterBuffer]` debug line, one per tick while any jitter-buffer field is present or the
 *   detector has a measured verdict: real jitter-buffer counters, standing depth in frames and
 *   ms, decoder identity, frame size, rVFC pipeline, hint read-back, verdict + transition.
 * - `[ResolutionSwitch]` debug line + `resolution-switch` health event on every decoded frame
 *   size change, with depth and glass-to-glass before/after (does a switch deepen the buffer?).
 * - `standing-buffer` / `standing-buffer-cleared` health events, one per edge.
 * Observability only — it never acts. Phase 4 reads `currentVerdict`; Phase 5 drains.
 */
export class JitterBufferDiagnosticsManager {
  private readonly nowMs: () => number;
  private readonly frameSizeByPeer = new Map<string, FrameSizeMemo>();
  /** The latest guest-end reading per peer, for the Beacon debug overlay. */
  private readonly guestLinkByPeer = new Map<string, GuestLinkDebugSnapshot>();

  constructor(private readonly deps: JitterBufferDiagnosticsDeps) {
    this.nowMs = deps.nowMs ?? (() => Date.now());
  }

  /** Observe one guest clientStats tick with the host's own view of the peer. */
  public observe(peerId: string, payload: ClientStreamStatsPayload, host: JitterBufferHostContext): StandingLatencyVerdict {
    const inputs = toStandingInputs(payload, host);
    const observation = this.deps.detector.observe(peerId, inputs);
    const depthMs = standingDepthMs(payload, host.deliveredFps);

    this.observeFrameSize(peerId, payload, host, depthMs);
    this.guestLinkByPeer.set(peerId, guestLinkSnapshot(payload, host));
    if (payload.statKeys) {
      // Once per connection: the stats this engine really reports, which the capability matrix only assumes.
      this.deps.debugLog('[GuestStatKeys]', { peerId, engine: payload.guestEngine ?? null, ...payload.statKeys });
    }
    if (hasDiagnosticField(payload) || observation.verdict.kind !== 'unknown') {
      this.deps.debugLog('[JitterBuffer]', jitterBufferLine(peerId, payload, host, depthMs, observation));
    }
    this.emitStandingEdges(peerId, observation, inputs, depthMs);
    this.deps.onStandingTransition?.(peerId, observation.verdict, observation.transition, {
      frames: payload.standingBacklogFrames ?? null,
      ms: depthMs ?? null,
    });
    return observation.verdict;
  }

  /** The detector's latest verdict for a peer, or undefined when never observed. */
  public currentVerdict(peerId: string): StandingLatencyVerdict | undefined {
    return this.deps.detector.current(peerId);
  }

  /** The guest's latest codec / jitter-buffer / glass-to-glass reading, for the overlay. */
  public guestLink(peerId: string): GuestLinkDebugSnapshot | undefined {
    return this.guestLinkByPeer.get(peerId);
  }

  public clearPeer(peerId: string): void {
    this.deps.detector.clearPeer(peerId);
    this.frameSizeByPeer.delete(peerId);
    this.guestLinkByPeer.delete(peerId);
  }

  /** Health event on the bus, plus the same `[HostHealth.event]` mirror line PCM writes for downgrade events. */
  private emitHealth(peerId: string, kind: StreamHealthEventKind, detail: JitterBufferHealthDetail): void {
    this.deps.emitHealthEvent(peerId, kind, detail);
    this.deps.debugLog('[HostHealth.event]', { peerId, kind, stream: 'video', atMs: this.nowMs(), detail });
  }

  private observeFrameSize(
    peerId: string,
    payload: ClientStreamStatsPayload,
    host: JitterBufferHostContext,
    depthMs: number | undefined,
  ): void {
    if (!isFiniteNumber(payload.frameWidth) || !isFiniteNumber(payload.frameHeight)) return;
    const current: FrameSizeMemo = {
      width: payload.frameWidth,
      height: payload.frameHeight,
      depthFrames: payload.standingBacklogFrames,
      depthMs,
      g2gMs: host.g2gLastMs ?? undefined,
    };
    const previous = this.frameSizeByPeer.get(peerId);
    this.frameSizeByPeer.set(peerId, current);
    if (!previous || (previous.width === current.width && previous.height === current.height)) return;

    const detail = compact({
      from: frameLabel(previous),
      to: frameLabel(current),
      depthBeforeFrames: previous.depthFrames,
      depthBeforeMs: previous.depthMs,
      depthAfterFrames: current.depthFrames,
      depthAfterMs: current.depthMs,
      g2gBeforeMs: previous.g2gMs,
      g2gAfterMs: current.g2gMs,
    });
    this.deps.debugLog('[ResolutionSwitch]', { peerId, ...detail });
    this.emitHealth(peerId, 'resolution-switch', detail);
  }

  private emitStandingEdges(
    peerId: string,
    observation: StandingLatencyObservation,
    inputs: StandingLatencyInputs,
    depthMs: number | undefined,
  ): void {
    const { verdict, transition, ended } = observation;
    if (transition === 'began') {
      this.emitHealth(
        peerId,
        'standing-buffer',
        compact({
          excessMs: verdict.excessMs ?? undefined,
          depthHypothesis: verdict.depthHypothesis,
          standingBacklogFrames: inputs.standingBacklogFrames,
          standingBacklogMs: depthMs,
          g2gFloorMs: inputs.g2gFloorMs,
          g2gLastMs: inputs.g2gLastMs,
          rttFloorMs: inputs.rttFloorMs,
          deliveredFps: inputs.deliveredFps,
          atMs: this.nowMs(),
        }),
      );
    } else if (transition === 'ended' && ended) {
      this.emitHealth(
        peerId,
        'standing-buffer-cleared',
        compact({
          durationMs: Math.round(ended.durationMs),
          peakExcessMs: Math.round(ended.peakExcessMs * 10) / 10,
          kindNow: verdict.kind,
          excessNowMs: verdict.excessMs ?? undefined,
        }),
      );
    }
  }
}

function toStandingInputs(payload: ClientStreamStatsPayload, host: JitterBufferHostContext): StandingLatencyInputs {
  return compact({
    measurementFresh: host.measurementFresh === true,
    g2gLastMs: host.g2gLastMs ?? undefined,
    g2gFloorMs: host.g2gFloorMs ?? undefined,
    rttFloorMs: host.rttFloorMs ?? payload.rttFloorMs,
    standingQueueMs: payload.standingQueueMs,
    playoutLatencyTrendMs: payload.playoutLatencyTrendMs,
    packetsLostPct: payload.packetsLostPct,
    decodeMsPerFrame: payload.decodeMsPerFrame,
    deliveredFps: host.deliveredFps,
    standingBacklogFrames: payload.standingBacklogFrames,
    jitterBufferDelayMs: payload.jitterBufferDelayMs,
  });
}

/** Standing depth in ms: the real jitter-buffer delay when the guest sends it, else frames x frame interval. */
function standingDepthMs(payload: ClientStreamStatsPayload, deliveredFps: number | undefined): number | undefined {
  if (isFiniteNumber(payload.jitterBufferDelayMs)) return Math.round(payload.jitterBufferDelayMs);
  if (!isFiniteNumber(payload.standingBacklogFrames) || !isFiniteNumber(deliveredFps) || deliveredFps <= 0) return undefined;
  return Math.round((payload.standingBacklogFrames * 1000) / deliveredFps);
}

function jitterBufferLine(
  peerId: string,
  payload: ClientStreamStatsPayload,
  host: JitterBufferHostContext,
  depthMs: number | undefined,
  observation: StandingLatencyObservation,
): Record<string, unknown> {
  const size = isFiniteNumber(payload.frameWidth) && isFiniteNumber(payload.frameHeight) ? { width: payload.frameWidth, height: payload.frameHeight } : null;
  return {
    peerId,
    jbDelayMs: payload.jitterBufferDelayMs ?? null,
    jbTargetMs: payload.jitterBufferTargetDelayMs ?? null,
    jbMinMs: payload.jitterBufferMinimumDelayMs ?? null,
    standingBacklogFrames: payload.standingBacklogFrames ?? null,
    standingBacklogMs: depthMs ?? null,
    deliveredFps: host.deliveredFps ?? null,
    decodeFps: payload.decodeFps ?? null,
    engine: payload.guestEngine ?? null,
    // Guest side: what it is decoding (never permission-gated). Host side: what was negotiated
    // for the send. They should name the same codec; a mismatch is itself a finding.
    codec: { guest: payload.codecMimeType ?? null, host: host.sendCodec ?? null },
    // A target step-up a few seconds after a keyframe is the estimator's max_frame_size term.
    sinceKeyframeMs: host.sinceKeyframeMs ?? null,
    keyframesWithheld: host.keyframesWithheld ?? 0,
    // The network's own jitter figure: a target far above it is the estimator, not the link.
    jitterMs: payload.jitterMs ?? null,
    // The estimator's frame-size term, as received: `(max - avg) x learned slope` is the target.
    frames: payload.encodedFrames
      ? {
          n: payload.encodedFrames.frames,
          avgBytes: payload.encodedFrames.avgBytes,
          maxBytes: payload.encodedFrames.maxBytes,
          keyframes: payload.encodedFrames.keyframes,
          maxKeyframeBytes: payload.encodedFrames.maxKeyframeBytes ?? null,
        }
      : null,
    // The audio (NetEq) buffer A/V sync could be holding the video buffer to.
    audioJb:
      payload.audioJitterBufferDelayMs !== undefined || payload.audioJitterBufferTargetDelayMs !== undefined
        ? { delayMs: payload.audioJitterBufferDelayMs ?? null, targetMs: payload.audioJitterBufferTargetDelayMs ?? null }
        : null,
    decoderImplementation: payload.decoderImplementation ?? null,
    powerEfficient: payload.powerEfficientDecoder ?? null,
    frame: size ? frameLabel(size) : null,
    pipeline: payload.framePipeline
      ? {
          recvToPresentP50: payload.framePipeline.receiveToPresentP50Ms ?? null,
          processingP50: payload.framePipeline.processingP50Ms ?? null,
          presentToDisplayP50: payload.framePipeline.presentToDisplayP50Ms ?? null,
        }
      : null,
    // What reached the glass: decodeFps can read 60 while presentedFps reads 36.
    present: payload.presentation ?? null,
    // Raw inbound-rtp deltas (drops, NACK/PLI/FIR, retransmits, discards), matrix-independent.
    rx: payload.receiverCounters ?? null,
    hint: payload.receiverHint
      ? { jitterBufferTargetMs: payload.receiverHint.jitterBufferTargetMs, playoutDelayHintMs: payload.receiverHint.playoutDelayHintMs }
      : null,
    g2gLastMs: host.g2gLastMs ?? null,
    g2gFloorMs: host.g2gFloorMs ?? null,
    standing: {
      kind: observation.verdict.kind,
      excessMs: observation.verdict.excessMs,
      depthHypothesis: observation.verdict.depthHypothesis,
      transition: observation.transition,
    },
  };
}

function hasDiagnosticField(payload: ClientStreamStatsPayload): boolean {
  return DIAGNOSTIC_FIELDS.some((field) => payload[field] !== undefined);
}

/** The overlay's view of one tick: only what was measured, nothing zero-filled. */
function guestLinkSnapshot(payload: ClientStreamStatsPayload, host: JitterBufferHostContext): GuestLinkDebugSnapshot {
  return {
    ...(payload.guestEngine !== undefined ? { engine: payload.guestEngine } : {}),
    ...(payload.codecMimeType !== undefined ? { codec: payload.codecMimeType } : {}),
    ...(isFiniteNumber(payload.jitterBufferDelayMs) ? { jitterBufferMs: payload.jitterBufferDelayMs } : {}),
    ...(isFiniteNumber(payload.jitterBufferTargetDelayMs) ? { jitterBufferTargetMs: payload.jitterBufferTargetDelayMs } : {}),
    ...(isFiniteNumber(payload.standingBacklogFrames) ? { standingFrames: payload.standingBacklogFrames } : {}),
    ...(isFiniteNumber(payload.jitterMs) ? { jitterMs: payload.jitterMs } : {}),
    ...(payload.encodedFrames
      ? { frameAvgBytes: payload.encodedFrames.avgBytes, frameMaxBytes: payload.encodedFrames.maxBytes }
      : {}),
    ...(isFiniteNumber(host.g2gLastMs) ? { glassToGlassMs: host.g2gLastMs } : {}),
    ...(isFiniteNumber(host.g2gFloorMs) ? { glassToGlassFloorMs: host.g2gFloorMs } : {}),
  };
}

function frameLabel(size: { width: number; height: number }): string {
  return `${size.width}x${size.height}`;
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/** Drop undefined values so records omit rather than zero-fill. */
function compact<T extends Record<string, unknown>>(record: T): { [K in keyof T]: Exclude<T[K], undefined> } {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) if (value !== undefined) out[key] = value;
  return out as { [K in keyof T]: Exclude<T[K], undefined> };
}
